import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import moment from 'moment';
import {
    memo, useCallback, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Header.scss';

import type Api from '~/types';
import type { RootState } from '~/store';

import { DWH_EXPORT_EVENTS, END_DELAY } from '~/constants';
import dwhExport from '~/dwhExport';

import {
    hideRepeatSidebar,
    repeatBuildingProcess as repeatBuildingProcessAction,
    showRepeatSidebar,
    stopRepeatBuildingProcess as stopRepeatBuildingProcessAction,
} from '~/store/app';
import { useAppDispatch, useAppSelector } from '~/hooks';
import { appIsReady, isQuestsComplete } from '~/selectors';
import { CountDown } from '~/Components';
import Link from '~/Components/Link/Link';
import { HeaderEndEventMessage, HeaderEndMessage } from '~/Components/Messages/Messages';
import Stage from '~/Components/Stage/Stage';

export interface HeaderState {
    progressFinishTime: number,
    isRepeating: boolean,
    isAnimationInProgress: boolean,
    isRepeatSidebarVisible: boolean,
    appIsReady: boolean,
    isQuestsComplete: boolean,
    shipLevel: number,
    shipLevels: Array<Api.ShipLevel>,
    animationShipLevel: number,
    closeTime: number,
    lastLevel: number,
    progress: number,
}

const stateSelector = (state: RootState): HeaderState => {
    return {
        progressFinishTime: state.app.progressFinishTime,
        isRepeating: state.app.isRepeating,
        isAnimationInProgress: state.app.isAnimationInProgress,
        isRepeatSidebarVisible: state.app.isRepeatSidebarVisible,
        shipLevel: state.app.shipLevel,
        appIsReady: appIsReady(state),
        isQuestsComplete: isQuestsComplete(state),
        shipLevels: state.app.shipLevels,
        animationShipLevel: state.app.animationShipLevel,
        closeTime: state.app.closeTime,
        lastLevel: state.app.lastLevel,
        progress: state.app.account?.progress || 0,
    };
};

const Header = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(stateSelector, equal);
    const [isReplayButtonVisible, setIsReplayButtonVisible] = useState<boolean>(false);
    const [isEndReached, setIsEndReached] = useState<boolean>(false);
    const {
        lastLevel,
        progressFinishTime,
        isRepeating,
        isAnimationInProgress,
        isRepeatSidebarVisible,
        shipLevel,
        appIsReady,
        isQuestsComplete,
        shipLevels,
        animationShipLevel,
        closeTime,
        progress,
    } = state;

    useEffect(() => {
        if (shipLevel > 0) {
            setIsReplayButtonVisible(true);
        }
    }, [shipLevel]);

    useEffect(() => {
        if (animationShipLevel === lastLevel) {
            setIsEndReached(true);
        } else {
            setIsEndReached(false);
        }
    }, [shipLevel, lastLevel, animationShipLevel]);

    const handleShowRepeatSidebar = useCallback(() => {
        dispatch(showRepeatSidebar());
    }, [dispatch]);
    const handleHideRepeatSidebar = useCallback(() => {
        dispatch(hideRepeatSidebar());
    }, [dispatch]);

    const repeatBuildingProcess = useCallback(() => {
        dispatch(repeatBuildingProcessAction());
        dwhExport.send(DWH_EXPORT_EVENTS.REPEAT_ANIMATION);
    }, [dispatch]);
    const stopRepeatBuildingProcess = useCallback(() => {
        dispatch(stopRepeatBuildingProcessAction(undefined, progress));
    }, [dispatch, progress]);

    const { t } = useTranslation();

    const onBurgerClick = useCallback(() => {
        if (isRepeatSidebarVisible) {
            handleHideRepeatSidebar();
        } else {
            handleShowRepeatSidebar();
        }
    }, [handleHideRepeatSidebar, handleShowRepeatSidebar, isRepeatSidebarVisible]);

    const getStageTooltipText = () => {
        let descr: string | undefined | null = '';
        shipLevels.forEach((shipLevel) => {
            if (shipLevel.level === animationShipLevel) {
                descr = shipLevel.description;
            }
        });
        return descr;
    };

    const now = moment.utc().unix() * 1000;
    const isEndTimerVisible = now < progressFinishTime && progressFinishTime - now < END_DELAY;
    const isEventFinished = now >= progressFinishTime && appIsReady;
    const isQuestsCompleteWithEvent = isQuestsComplete || isEventFinished;

    const wrapperClassname = classnames(styles.wrapper, {
        [styles.wrapperVisible]: appIsReady,
    });

    return (
        <>
            <div className={styles.topOverlay} />
            <div className={wrapperClassname}>
                <div className={styles.headerContent}>
                    <div className={styles.subtitle}>
                        <HeaderEndEventMessage isVisible={isEventFinished && !isEndReached} eventEndDate={closeTime} />
                        <HeaderEndMessage
                            lastLevel={lastLevel}
                            isVisible={isEndReached}
                            isQuestsComplete={isQuestsCompleteWithEvent}
                        />
                        {isEndReached || isEventFinished ? null : (
                            <Stage
                                isVisible={appIsReady}
                                shipLevel={animationShipLevel}
                                tooltipText={getStageTooltipText()}
                            />
                        )}
                        {isEndTimerVisible && (
                            <div className={styles.bigCountdown}>
                                <CountDown date={progressFinishTime} isLarge tooltipText={t('Время отведенное на строительство корабля')} />
                            </div>
                        )}
                        <div className={styles.actionButtons}>
                            {isReplayButtonVisible && (
                                <>
                                    {isRepeating ? (
                                        <Link onClick={stopRepeatBuildingProcess} icon={'stop'} text={t('Остановить просмотр строительства')} />
                                    ) : (
                                        <Link
                                            onClick={repeatBuildingProcess}
                                            icon={'play'}
                                            text={t('Посмотреть строительство сначала')}
                                            isDisabled={isAnimationInProgress}
                                        />
                                    )}
                                </>
                            )}
                            {isRepeatSidebarVisible ? (
                                <Link onClick={onBurgerClick} icon={'close'} text={t('Закрыть список этапов')} />
                            ) : (
                                <Link onClick={onBurgerClick} icon={'menu'} text={t('Открыть список этапов')} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(Header);
